import { inclusiveEndDateFromExclusive } from './supplierFactorInterval';

// The SINGLE canonical mapping from the versioned supplier factor list
// (GET /supplier/factors → items[], see supplierFactorContracts.js) to the
// flat "lite" factor view-model every authenticated /supplier surface
// renders from — MockDataContext (data.emissionFactors: gaps engine,
// InstallationsPage, SefaSavingsCard) and EmissionFactorsPage's own table.
// There is deliberately only one mapping function (B1) — the legacy
// per-installation `installationDocs[].emissionsFactors` arrays are never
// exposed as `data.emissionFactors` again.
//
// NOT used by the public CBAM Portal page/widget — those render the normalized
// public portal payload returned by the backend.
const str = (v) => (v == null ? '' : String(v));

const toDateStr = (v) => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

// A supplier factor list item (GET /supplier/factors → items[]) → the flat lite
// factor model every consumer renders. Carries the revision handles the factor
// surface needs: currentRevisionId (correct/withdraw), emissionsFactorId
// (edit-seed fetch), historyAnchorRevisionId (history), and the propagation
// snapshot (Sync badge).
export function revisionListItemToFlatFactor(item) {
  const seriesKey = item.seriesKey || {};
  const current = item.currentRevision || {};
  const emissions = current.emissions || {};
  return {
    // The current EmissionsFactor id doubles as the flat row identity (CgTable
    // keys selection/actions off `_id`, which the page sets from `id`).
    id: str(current.emissionsFactorId),
    emissionsFactorId: str(current.emissionsFactorId),
    currentRevisionId: str(current.id),
    historyAnchorRevisionId: str(item.historyAnchorRevisionId),
    revisionNumber: Number(current.revisionNumber) || 1,
    installationId: str(seriesKey.installationId),
    cnCode: str(seriesKey.cnCode),
    productId: str(seriesKey.productVariant),
    // The versioned series key only carries method CODES, not full parameter
    // records — `parameters` is deliberately always [] here (the revision
    // model has no equivalent of the legacy installationDocs' directParameters).
    productionMethods: (seriesKey.productionMethodCodes || []).map((code) => ({
      methodCode: code,
      name: code,
      parameters: [],
    })),
    periodFrom: toDateStr(item.interval?.startInclusive),
    // Half-open endExclusive → inclusive display end (endExclusive - 1 day).
    periodTo: inclusiveEndDateFromExclusive(item.interval?.endExclusive),
    seeDirect: Number(emissions.direct) || 0,
    seeIndirect: Number(emissions.indirect) || 0,
    total: Number(emissions.total) || 0,
    authoritativeSefa: current.authoritativeSefa || null,
    cbamFactor: Number(current.cbamFactor) || 0,
    reportingYear: Number(current.reportingYear) || null,
    // Free tier never computes a share-of-default-emissions figure from the
    // versioned model — deliberately 0 (see gaps.js `default_data`, which this
    // keeps inert rather than fed a legacy-only field).
    shareDefaultEmissions: 0,
    source: current.origin === 'template' ? 'Communication template' : 'Manual entry',
    // Verification is a premium feature — always unverified on free tier.
    verified: false,
    // The async propagation snapshot for the Sync badge.
    propagation: item.propagation || null,
  };
}

// GET /supplier/factors → items[] → the flat lite factor list, in list order.
export const supplierFactorsToFlatFactors = (items) =>
  (items || []).map(revisionListItemToFlatFactor);
